import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import LeftPart from './LeftPart/LeftPart'
import RightPart from './RightPart/RightPart'
import ReportCalendarCreateReportDrawer from './Create/ReportCalendarCreateReportDrawer'
import ReportCalendarReportDetailsDrawer from './Details/ReportCalendarReportDetailsDrawer'
import {
	buildReportDetailsFromTimelineTag,
	buildReportDetailsFromUpcomingItem,
} from './Details/reportCalendarReportDetails'
import {
	fetchReportCalendarFilters,
	fetchReportCalendarMonth,
	fetchReportCalendarTimeline,
	fetchReportCalendarUpcoming,
} from './reportCalendarApi'
import {
	formatTimelineDateHeader,
	getAdjacentEventDate,
	getTimelineDatesFromAnchor,
	getTimelineQueryRange,
	getUpcomingQueryRange,
	startOfDay,
} from './reportCalendarDateUtils'
import {
	buildTimelineCompaniesFromApi,
	buildUpcomingReportsGroupsFromApi,
	buildReportCalendarFilterQuery,
	mapCalendarDatesToEvents,
	mapTimelineDatesToEvents,
	mapTimelineItemsToEvents,
	mergeCalendarPickerEvents,
	normalizeReportCalendarFilters,
	REPORT_CALENDAR_INITIAL_FILTER_VALUES,
} from './reportCalendarMappers'
import './styles/report-calendar-main.sass'

const TIMELINE_COLUMNS_LIMIT = 6

const getDocumentTopOffset = (element) => {
	const { top } = element.getBoundingClientRect()
	return Math.max(0, Math.ceil(top + window.scrollY))
}

const ReportCalendarMain = () => {
	const calendarRef = useRef(null)
	const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()))
	const [viewMonth, setViewMonth] = useState(() => {
		const today = new Date()
		return { year: today.getFullYear(), month: today.getMonth() + 1 }
	})
	const [calendarDates, setCalendarDates] = useState([])
	const [timelinePayload, setTimelinePayload] = useState({
		dates: [],
		businesses: [],
		items: [],
	})
	const [upcomingMonths, setUpcomingMonths] = useState([])
	const [isCalendarLoading, setIsCalendarLoading] = useState(true)
	const [isTimelineLoading, setIsTimelineLoading] = useState(true)
	const [isUpcomingLoading, setIsUpcomingLoading] = useState(true)
	const [filterValues, setFilterValues] = useState(REPORT_CALENDAR_INITIAL_FILTER_VALUES)
	const [filterOptions, setFilterOptions] = useState({
		businesses: [],
		jurisdictions: [],
		reportTypes: [],
		statuses: [],
	})
	const [isCreateReportOpen, setIsCreateReportOpen] = useState(false)
	const [selectedReportDetails, setSelectedReportDetails] = useState(null)
	const filterQuery = useMemo(
		() => buildReportCalendarFilterQuery(filterValues),
		[filterValues],
	)

	useEffect(() => {
		const element = calendarRef.current

		if (!element) {
			return undefined
		}

		const updateMinHeight = () => {
			const topOffset = getDocumentTopOffset(element)
			element.style.setProperty(
				'--reporting-calendar-min-height',
				`calc(100vh - ${topOffset}px)`,
			)
		}

		const scheduleUpdate = () => {
			requestAnimationFrame(updateMinHeight)
		}

		scheduleUpdate()
		window.addEventListener('resize', scheduleUpdate)

		const resizeObserver = typeof ResizeObserver !== 'undefined'
			? new ResizeObserver(scheduleUpdate)
			: null
		const topbar = element.previousElementSibling

		if (resizeObserver) {
			resizeObserver.observe(document.documentElement)

			if (topbar) {
				resizeObserver.observe(topbar)
			}
		}

		return () => {
			window.removeEventListener('resize', scheduleUpdate)
			resizeObserver?.disconnect()
		}
	}, [])

	useEffect(() => {
		let isCurrent = true

		fetchReportCalendarFilters()
			.then((payload) => {
				if (isCurrent) {
					setFilterOptions(normalizeReportCalendarFilters(payload))
				}
			})
			.catch(() => {
				if (isCurrent) {
					setFilterOptions({
						businesses: [],
						jurisdictions: [],
						reportTypes: [],
						statuses: [],
					})
				}
			})

		return () => {
			isCurrent = false
		}
	}, [])

	useEffect(() => {
		let isCurrent = true
		setIsCalendarLoading(true)

		fetchReportCalendarMonth({
			...viewMonth,
			...filterQuery,
		})
			.then((payload) => {
				if (isCurrent) {
					setCalendarDates(payload?.dates ?? [])
				}
			})
			.catch(() => {
				if (isCurrent) {
					setCalendarDates([])
				}
			})
			.finally(() => {
				if (isCurrent) {
					setIsCalendarLoading(false)
				}
			})

		return () => {
			isCurrent = false
		}
	}, [viewMonth, filterQuery])

	useEffect(() => {
		let isCurrent = true
		const timelineRange = getTimelineQueryRange(selectedDate)
		const upcomingRange = getUpcomingQueryRange(selectedDate)

		setIsTimelineLoading(true)
		setIsUpcomingLoading(true)

		fetchReportCalendarTimeline({
			...timelineRange,
			...filterQuery,
		})
			.then((payload) => {
				if (isCurrent) {
					setTimelinePayload({
						dates: payload?.dates ?? [],
						businesses: payload?.businesses ?? [],
						items: payload?.items ?? [],
					})
				}
			})
			.catch(() => {
				if (isCurrent) {
					setTimelinePayload({ dates: [], businesses: [], items: [] })
				}
			})
			.finally(() => {
				if (isCurrent) {
					setIsTimelineLoading(false)
				}
			})

		fetchReportCalendarUpcoming({
			...upcomingRange,
			...filterQuery,
		})
			.then((payload) => {
				if (isCurrent) {
					setUpcomingMonths(payload?.months ?? [])
				}
			})
			.catch(() => {
				if (isCurrent) {
					setUpcomingMonths([])
				}
			})
			.finally(() => {
				if (isCurrent) {
					setIsUpcomingLoading(false)
				}
			})

		return () => {
			isCurrent = false
		}
	}, [selectedDate, filterQuery])

	const calendarEvents = useMemo(
		() => mapCalendarDatesToEvents(calendarDates),
		[calendarDates],
	)

	const timelineEvents = useMemo(
		() => mapTimelineDatesToEvents(timelinePayload.dates),
		[timelinePayload.dates],
	)

	const pickerEvents = useMemo(() => {
		const upcomingItems = upcomingMonths.flatMap((month) => month.items ?? [])

		return mergeCalendarPickerEvents(
			calendarEvents,
			[
				...mapTimelineItemsToEvents(timelinePayload.items),
				...mapTimelineItemsToEvents(upcomingItems),
			],
		)
	}, [calendarEvents, timelinePayload.items, upcomingMonths])

	const timelineSourceDates = useMemo(() => (
		getTimelineDatesFromAnchor(
			selectedDate,
			timelineEvents,
			TIMELINE_COLUMNS_LIMIT,
		)
	), [selectedDate, timelineEvents])

	const timelineDates = useMemo(
		() => timelineSourceDates.map(formatTimelineDateHeader),
		[timelineSourceDates],
	)

	const timelineCompanies = useMemo(
		() => buildTimelineCompaniesFromApi(
			timelineSourceDates,
			timelinePayload.businesses,
			timelinePayload.items,
		),
		[timelineSourceDates, timelinePayload.businesses, timelinePayload.items],
	)

	const upcomingReportsGroups = useMemo(
		() => buildUpcomingReportsGroupsFromApi(upcomingMonths),
		[upcomingMonths],
	)

	const canGoToPreviousTimelineDate = useMemo(
		() => getAdjacentEventDate(selectedDate, timelineEvents, 'previous') !== null,
		[selectedDate, timelineEvents],
	)

	const canGoToNextTimelineDate = useMemo(
		() => getAdjacentEventDate(selectedDate, timelineEvents, 'next') !== null,
		[selectedDate, timelineEvents],
	)

	const handleFilterChange = useCallback((key, value) => {
		setFilterValues((current) => ({
			...current,
			[key]: value,
		}))
	}, [])

	const handleViewMonthChange = useCallback((date) => {
		setViewMonth((current) => {
			const nextYear = date.getFullYear()
			const nextMonth = date.getMonth() + 1

			if (current.year === nextYear && current.month === nextMonth) {
				return current
			}

			return { year: nextYear, month: nextMonth }
		})
	}, [])

	const handlePreviousTimelineDate = useCallback(() => {
		const previousDate = getAdjacentEventDate(
			selectedDate,
			timelineEvents,
			'previous',
		)

		if (previousDate) {
			setSelectedDate(previousDate)
		}
	}, [selectedDate, timelineEvents])

	const handleNextTimelineDate = useCallback(() => {
		const nextDate = getAdjacentEventDate(
			selectedDate,
			timelineEvents,
			'next',
		)

		if (nextDate) {
			setSelectedDate(nextDate)
		}
	}, [selectedDate, timelineEvents])

	const handleOpenCreateReport = useCallback(() => {
		setIsCreateReportOpen(true)
	}, [])

	const handleCloseCreateReport = useCallback(() => {
		setIsCreateReportOpen(false)
	}, [])

	const handleTimelineReportSelect = useCallback((selection) => {
		setSelectedReportDetails(buildReportDetailsFromTimelineTag(selection))
	}, [])

	const handleUpcomingReportSelect = useCallback((item) => {
		setSelectedReportDetails(buildReportDetailsFromUpcomingItem(item))
	}, [])

	const handleCloseReportDetails = useCallback(() => {
		setSelectedReportDetails(null)
	}, [])

	const handleReportSelect = useCallback((payload) => {
		if (payload?.tag) {
			handleTimelineReportSelect(payload)
			return
		}

		handleUpcomingReportSelect(payload)
	}, [handleTimelineReportSelect, handleUpcomingReportSelect])

	return (
		<div ref={calendarRef} className='reporting-calendar'>
			<div className='reporting-calendar__left'>
				<LeftPart
					selectedDate={selectedDate}
					onSelectedDateChange={setSelectedDate}
					onViewMonthChange={handleViewMonthChange}
					events={pickerEvents}
					isLoading={isCalendarLoading}
				/>
			</div>

			<div className='reporting-calendar__right'>
				<RightPart
					timelineDates={timelineDates}
					timelineSourceDates={timelineSourceDates}
					timelineCompanies={timelineCompanies}
					upcomingReportsGroups={upcomingReportsGroups}
					canGoToPreviousTimelineDate={canGoToPreviousTimelineDate}
					canGoToNextTimelineDate={canGoToNextTimelineDate}
					isTimelineLoading={isTimelineLoading}
					isUpcomingLoading={isUpcomingLoading}
					filterValues={filterValues}
					filterOptions={filterOptions}
					onFilterChange={handleFilterChange}
					onPreviousTimelineDate={handlePreviousTimelineDate}
					onNextTimelineDate={handleNextTimelineDate}
					onCreate={handleOpenCreateReport}
					onReportSelect={handleReportSelect}
				/>
			</div>

			<ReportCalendarCreateReportDrawer
				open={isCreateReportOpen}
				onClose={handleCloseCreateReport}
				defaultDate={selectedDate}
			/>

			<ReportCalendarReportDetailsDrawer
				open={Boolean(selectedReportDetails)}
				report={selectedReportDetails}
				onClose={handleCloseReportDetails}
			/>
		</div>
	)
}

export default ReportCalendarMain
